import { useCallback, useMemo, useState } from "react";

import {
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Modal,
  ModalProps,
  Stack,
  StackProps,
  Text,
  TextProps,
  Title,
  TitleProps,
} from "@mantine/core";
import { IconBolt } from "@tabler/icons-react";
import { getEcsShareLinkId } from "app/lib/heap/tracking";
import { MantineTabler } from "app/ui/components/mantine-tabler";
import { useSafeDelayedValueOnModalClose } from "app/ui/hooks/useDelayedValue";

export type EcsUpsellFeatureName = "survey-results";

export type EcsUpsellModalState =
  | {
      feature: EcsUpsellFeatureName;
      opened: true;
    }
  | {
      feature: null;
      opened: false;
    };

const EcsUpsellModalCopy: Partial<
  Record<EcsUpsellFeatureName, { title: string; description: string }>
> = {
  "survey-results": {
    title: "See Full Results",
    description:
      "Participate to access benchmarks for all executive roles, AI-specific positions, and BOD data. Use powerful filtering tools to find the most relevant comparisons to your company.",
  },
};

export function useEcsUpsellModalDisclosure(): [
  EcsUpsellModalState,
  {
    open: (feature: EcsUpsellFeatureName) => void;
    close: () => void;
  },
] {
  const [state, setState] = useState<EcsUpsellModalState>({
    opened: false,
    feature: null,
  });

  const open = useCallback(
    (feature: EcsUpsellFeatureName) => {
      setState({ opened: true, feature });
    },
    [setState],
  );

  const close = useCallback(() => {
    setState({ opened: false, feature: null });
  }, [setState]);

  const handlers = useMemo(() => ({ open, close }), [open, close]);

  return [state, handlers];
}

function EcsUpsellTitle(props: TitleProps) {
  return <Title order={3} {...props} />;
}

function EcsUpsellDesc(props: TextProps) {
  return <Text fz={"sm"} c={"gray.6"} {...props} />;
}

function EcsUpsellStack(props: StackProps) {
  return <Stack align={"center"} justify={"center"} ta={"center"} {...props} />;
}

function EcsUpsellBadge() {
  return (
    <Badge color="green" sx={{ textTransform: "none" }} size="lg">
      <Group gap={4}>
        <MantineTabler icon={IconBolt} color="yellow.5" size={16} />
        Full Access
      </Group>
    </Badge>
  );
}

function EcsUpsellModalInnerContent({ feature }: { feature: EcsUpsellFeatureName }) {
  const featureCopy = EcsUpsellModalCopy[feature as keyof typeof EcsUpsellModalCopy];

  if (!feature || !featureCopy) {
    return null;
  }

  return (
    <>
      <EcsUpsellStack>
        <EcsUpsellBadge />
        <EcsUpsellTitle>{featureCopy.title}</EcsUpsellTitle>
        <EcsUpsellDesc>{featureCopy.description}</EcsUpsellDesc>
      </EcsUpsellStack>
    </>
  );
}

function EcsUpsellCtas({ learnMoreLink }: { learnMoreLink: string }) {
  // Preserve share link ID in the URL if it exists
  const shareLinkId = getEcsShareLinkId();
  const finalLink = shareLinkId
    ? `${learnMoreLink}${learnMoreLink.includes("?") ? "&" : "?"}from_share=${shareLinkId}`
    : learnMoreLink;

  return (
    <Stack gap={24}>
      <Divider color="gray.2" />
      <Group justify="center" align="center" gap={12}>
        <Button size="md" variant="primary" component="a" target="_blank" href={finalLink}>
          Participate to unlock
        </Button>
      </Group>
    </Stack>
  );
}

function EcsUpsellModalContent({ feature }: { feature: EcsUpsellFeatureName }) {
  if (!feature || !EcsUpsellModalCopy[feature as keyof typeof EcsUpsellModalCopy]) {
    return null;
  }
  return <EcsUpsellModalInnerContent feature={feature} />;
}

export function EcsUpsellModal({
  feature: unsafeFeature,
  ...modalProps
}: Omit<ModalProps, "opened"> & EcsUpsellModalState) {
  const feature = useSafeDelayedValueOnModalClose(unsafeFeature);
  const learnMoreLink = "/ecs";

  return (
    <Modal
      {...modalProps}
      size="sm"
      p={"xs"}
      styles={{
        header: {
          // Hide some stuff
          borderBottomWidth: 0,
          marginBottom: `calc(var(--mantine-spacing-xl) * -1)`,
        },
      }}
    >
      <Stack align="center" justify="center" gap={24}>
        <Box mb={22} px={18}>
          <EcsUpsellModalContent feature={feature as EcsUpsellFeatureName} />
        </Box>
      </Stack>
      <EcsUpsellCtas learnMoreLink={learnMoreLink} />
    </Modal>
  );
}
